import { turnstileUrl } from '@/config/runtime-config';

/**
 * The three Turnstile endpoints the portal needs. Turnstile owns *who a person
 * is* and nothing else: the token it returns carries identity claims only, and
 * Keystone is what decides whether that identity may read anything.
 *
 * Every call sends credentials, because the session lives in an httpOnly
 * cookie Turnstile sets on its own host. That is also why the responses are
 * the only place the identity token exists — it is deliberately not persisted
 * (see `session.ts`).
 */

export interface ChallengeResponse {
  challenge_id: string;
}

export interface TokenResponse {
  identity_token: string;
  token_type: string;
  expires_in: number;
}

export class TurnstileError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'TurnstileError';
  }
}

/** Raised by `refresh()` when there is no session cookie to refresh. */
export class NoSessionError extends TurnstileError {
  constructor() {
    super(401, 'session_not_found', 'No Turnstile session');
    this.name = 'NoSessionError';
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  return send<T>('POST', path, body);
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${turnstileUrl()}/${path}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    throw new TurnstileError(
      response.status,
      await errorCode(response),
      `${method} /${path} failed`,
    );
  }

  // 204 (logout) has no body to parse.
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

async function errorCode(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { detail?: { code?: string }; code?: string };
    return body?.detail?.code ?? body?.code;
  } catch {
    return undefined;
  }
}

/** Send a one-time pin to `email`. Answers 202 whether or not it was sent. */
export function challenge(email: string): Promise<ChallengeResponse> {
  return post<ChallengeResponse>('session/challenge', {
    identifier: { type: 'email', value: email },
  });
}

/** Exchange a pin for an identity token, opening a session cookie. */
export function verify(challengeId: string, solution: string): Promise<TokenResponse> {
  return post<TokenResponse>('session/challenge/verify', {
    challenge_id: challengeId,
    solution,
  });
}

/**
 * Mint a fresh identity token off the session cookie. This is how a reload
 * stays logged in, and how an expiring token is replaced mid-session.
 * A caller who never logged in gets `NoSessionError`, which is an ordinary
 * first-visit answer rather than a failure worth surfacing.
 */
export async function refresh(): Promise<TokenResponse> {
  try {
    return await send<TokenResponse>('GET', 'session');
  } catch (error) {
    if (error instanceof TurnstileError && error.status === 401) {
      throw new NoSessionError();
    }
    throw error;
  }
}

/** Revoke the session on this issuer and clear its cookie. */
export function logout(token: string): Promise<void> {
  return fetch(`${turnstileUrl()}/session/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${token}` },
  }).then(() => undefined);
}
